import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Receipt, ScrollText, FileText, Clock, Car, TrendingUp, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import { API } from '@/utils/api';
import axios from 'axios';

const INVOICE_STATUS = {
  brouillon: { label: 'Brouillon', color: 'bg-slate-100 text-slate-600' },
  envoyee: { label: 'Envoyee', color: 'bg-blue-100 text-blue-700' },
  payee: { label: 'Payee', color: 'bg-emerald-100 text-emerald-700' },
  annulee: { label: 'Annulee', color: 'bg-red-100 text-red-700' },
};

const QUOTE_STATUS = {
  brouillon: { label: 'Brouillon', color: 'bg-slate-100 text-slate-600' },
  envoye: { label: 'Envoye', color: 'bg-blue-100 text-blue-700' },
  accepte: { label: 'Accepte', color: 'bg-emerald-100 text-emerald-700' },
  refuse: { label: 'Refuse', color: 'bg-red-100 text-red-700' },
};

const WO_STATUS = {
  open: { label: 'A venir', color: 'bg-blue-100 text-blue-700' },
  signed: { label: 'Signee', color: 'bg-emerald-100 text-emerald-700' },
  in_progress: { label: 'En cours', color: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Terminee', color: 'bg-slate-100 text-slate-700' },
};

export const ClientHistorySection = ({ clientId }) => {
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    const load = async () => {
      setLoading(true);
      try {
        const [invRes, quoteRes, woRes] = await Promise.all([
          axios.get(`${API}/billing/invoices`, { params: { client_id: clientId } }),
          axios.get(`${API}/billing/quotes`, { params: { client_id: clientId } }),
          axios.get(`${API}/clients/${clientId}/interventions`),
        ]);
        setInvoices(invRes.data || []);
        setQuotes(quoteRes.data || []);
        setWorkOrders(woRes.data || []);
      } catch (err) {
        console.error('Error loading client history:', err);
      }
      setLoading(false);
    };
    load();
  }, [clientId]);

  // Build unified timeline
  const allItems = [];

  invoices.forEach(inv => {
    allItems.push({
      type: 'invoice', id: inv.id, date: inv.created_at || '',
      number: inv.number || '', amount: inv.total || 0,
      status: inv.status || 'brouillon', vehicle: inv.vehicle_info || '',
      description: `Facture ${inv.number || ''}`,
    });
  });

  quotes.forEach(q => {
    allItems.push({
      type: 'quote', id: q.id, date: q.created_at || '',
      number: q.number || '', amount: q.total || 0,
      status: q.status || 'brouillon', vehicle: q.vehicle_info || '',
      description: `Devis ${q.number || ''}`,
    });
  });

  workOrders.forEach(wo => {
    allItems.push({
      type: 'workorder', id: wo.id, date: wo.date || wo.created_at || '',
      number: wo.invoice_number || '', amount: wo.invoice_amount || 0,
      status: wo.status || 'open', vehicle: wo.vehicle_info || '',
      description: wo.description || 'Intervention',
    });
  });

  allItems.sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  const filtered = activeFilter === 'all' ? allItems :
    allItems.filter(item => item.type === activeFilter);

  const displayed = showAll ? filtered : filtered.slice(0, 10);
  const hiddenCount = filtered.length - 10;

  const totalInvoiced = invoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
  const totalQuoted = quotes.reduce((sum, q) => sum + (q.total || 0), 0);

  if (loading) {
    return <Card className="p-6 rounded-xl text-center text-slate-400 text-sm">Chargement de l'historique...</Card>;
  }

  return (
    <div className="space-y-4" data-testid="client-history-360">
      {/* Read-only indicator */}
      <div className="flex items-center gap-2 text-xs text-slate-400 px-1">
        <Eye className="w-3.5 h-3.5" />
        <span>Consultation uniquement</span>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-3 rounded-xl border-l-4 border-l-blue-500" data-testid="history-stat-invoices">
          <div className="flex items-center gap-2">
            <Receipt className="w-4 h-4 text-blue-600" />
            <span className="text-xs font-medium text-slate-500">Factures</span>
          </div>
          <p className="text-xl font-bold text-slate-900 mt-1">{invoices.length}</p>
          <p className="text-xs text-slate-400">CHF {totalInvoiced.toLocaleString('fr-CH', {minimumFractionDigits: 2})}</p>
        </Card>
        <Card className="p-3 rounded-xl border-l-4 border-l-amber-500" data-testid="history-stat-quotes">
          <div className="flex items-center gap-2">
            <ScrollText className="w-4 h-4 text-amber-600" />
            <span className="text-xs font-medium text-slate-500">Devis</span>
          </div>
          <p className="text-xl font-bold text-slate-900 mt-1">{quotes.length}</p>
          <p className="text-xs text-slate-400">CHF {totalQuoted.toLocaleString('fr-CH', {minimumFractionDigits: 2})}</p>
        </Card>
        <Card className="p-3 rounded-xl border-l-4 border-l-emerald-500" data-testid="history-stat-interventions">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-emerald-600" />
            <span className="text-xs font-medium text-slate-500">Interventions</span>
          </div>
          <p className="text-xl font-bold text-slate-900 mt-1">{workOrders.length}</p>
          <p className="text-xs text-slate-400">{workOrders.filter(w => w.status === 'completed').length} terminee(s)</p>
        </Card>
      </div>

      {/* Filter pills */}
      <div className="flex gap-2 flex-wrap" data-testid="history-filters">
        {[
          { key: 'all', label: 'Tout', count: allItems.length, color: 'bg-slate-900' },
          { key: 'invoice', label: 'Factures', count: invoices.length, color: 'bg-blue-600' },
          { key: 'quote', label: 'Devis', count: quotes.length, color: 'bg-amber-600' },
          { key: 'workorder', label: 'Interventions', count: workOrders.length, color: 'bg-emerald-600' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => { setActiveFilter(f.key); setShowAll(false); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
              activeFilter === f.key
                ? `${f.color} text-white shadow-sm`
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid={`history-filter-${f.key}`}
          >
            {f.label}
            <span className={`text-[10px] px-1.5 rounded-full ${activeFilter === f.key ? 'bg-white/20' : 'bg-slate-200'}`}>
              {f.count}
            </span>
          </button>
        ))}
      </div>

      {/* Timeline - READ ONLY */}
      {filtered.length === 0 ? (
        <Card className="p-8 text-center rounded-xl">
          <TrendingUp className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          <p className="text-sm text-slate-400">Aucun historique</p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="history-timeline">
          {displayed.map((item) => (
            <div
              key={`${item.type}-${item.id}`}
              className="p-3 rounded-xl border border-slate-200 bg-white"
              data-testid={`history-item-${item.type}-${item.id}`}
            >
              <div className="flex items-center gap-3">
                {/* Type icon */}
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  item.type === 'invoice' ? 'bg-blue-100 text-blue-600' :
                  item.type === 'quote' ? 'bg-amber-100 text-amber-600' :
                  'bg-emerald-100 text-emerald-600'
                }`}>
                  {item.type === 'invoice' ? <Receipt className="w-4 h-4" /> :
                   item.type === 'quote' ? <ScrollText className="w-4 h-4" /> :
                   <FileText className="w-4 h-4" />}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-slate-800">
                      {item.type === 'invoice' ? 'Facture' :
                       item.type === 'quote' ? 'Devis' : 'Intervention'}
                    </span>
                    {item.number && (
                      <span className="text-xs font-mono font-bold text-indigo-600">{item.number}</span>
                    )}
                    <Badge className={`text-[10px] ${
                      item.type === 'invoice' ? (INVOICE_STATUS[item.status]?.color || 'bg-slate-100 text-slate-600') :
                      item.type === 'quote' ? (QUOTE_STATUS[item.status]?.color || 'bg-slate-100 text-slate-600') :
                      (WO_STATUS[item.status]?.color || 'bg-slate-100 text-slate-600')
                    }`}>
                      {item.type === 'invoice' ? (INVOICE_STATUS[item.status]?.label || item.status) :
                       item.type === 'quote' ? (QUOTE_STATUS[item.status]?.label || item.status) :
                       (WO_STATUS[item.status]?.label || item.status)}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {item.date ? new Date(item.date).toLocaleDateString('fr-CH', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                    </span>
                    {item.vehicle && (
                      <span className="flex items-center gap-1">
                        <Car className="w-3 h-3" /> {item.vehicle}
                      </span>
                    )}
                    {item.type === 'workorder' && item.description && (
                      <span className="truncate max-w-[200px]">{item.description}</span>
                    )}
                  </div>
                </div>

                {/* Amount */}
                <div className="flex-shrink-0">
                  {item.amount > 0 && (
                    <span className="text-sm font-bold text-slate-800">
                      CHF {item.amount.toLocaleString('fr-CH', {minimumFractionDigits: 2})}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}

          {filtered.length > 10 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="w-full text-center text-xs text-indigo-600 hover:text-indigo-800 font-medium py-2 flex items-center justify-center gap-1"
              data-testid="history-show-more"
            >
              {showAll ? (
                <><ChevronUp className="w-3 h-3" /> Reduire</>
              ) : (
                <><ChevronDown className="w-3 h-3" /> Voir {hiddenCount} element(s) de plus...</>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
